import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useQuery } from "react-query";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

import useSnackbar from "../../../components/snackbar/hooks/useSnackbar";
import Stepper from "../../../components/stepper";
import useStepper from "../../../components/stepper/hooks/useStepper";
import OptionsContainer from "./OptionsContainer";
import DesignSummary from "./DesignSummary";
import OptionsFilter from "./OptionsFilter";
import FavoritesPanel from "./FavoritesPanel";
import DimensionFilter from "./DimensionFilter";
import OptionsShuffle from "./OptionsShuffle";
import Table from "../../../components/table";
import ServiceInfo from "../../../components/ServiceInfo";
import { formatToCurrency } from "../../../utils/numbers";
import { getErrorMessage } from "../../../utils/errors";
import {
  generateDesignShapesAndMaterialsSteps,
  getMaterialsFromComposition,
  getShapesFromComposition,
  isMaterialInComposition,
  isShapeInComposition,
} from "../../../utils/designs";
import { DESIGN_CATEGORY, LAYOUT_TYPES } from "../../../constants/designs";
import {
  MEASUREMENTS_ICON_URL,
  MATERIALS_ICON_URL,
  COLORS_ICON_URL,
} from "../../../constants/iconsUrls";
import { ELEMENTS_QUERY_KEY } from "../../../queries-keys/designElements";
import {
  updateCompositionWithLayout,
  updateCompositionWithShape,
  updateCompositionWithMaterial,
  DesignState,
} from "../../../features/designSlice";
import { CURRENCY } from "../../../constants/common";
import { getElementsByLayout } from "../../../apis/designElementsApi";
import useMediaQuery from "../../../hooks/useMediaQuery";
import useFilter from "../hooks/useFilter";
import Shape from "../../../models/furniture/Shape";
import Material from "../../../models/furniture/Material";
import useGetDesignStepOptions from "../../../hooks/useGetDesignStepOptions";
import { DesignOutletContext } from "..";
import LayoutSize from "../../../models/furniture/LayoutSize";
import LayoutBody from "../../../models/furniture/LayoutBody";

export interface ShapeStep {
  label: string;
  data: {
    category: string;
    elementId: number;
    elementName: string;
    elementOrder: number;
  };
}

export interface MaterialStep {
  label: string;
  data: {
    category: string;
    materialName: string;
    elementId: number;
    elementName: string;
    elementOrder: number;
    materialOrder: number;
  };
}

const EMPTY_OPTIONS: (Shape | Material)[] = [];

function DesignsSelector() {
  const isMaxWidth1024 = useMediaQuery("(max-width: 1024px)");

  const { layouts, item } = useOutletContext<DesignOutletContext>() || {};

  const body_layout_items = layouts?.bodyLayouts;
  const size_layout_items = layouts?.sizeLayouts;

  const layoutStep = {
    icon: MEASUREMENTS_ICON_URL,
    label: DESIGN_CATEGORY.LAYOUT,
    subSteps: [
      {
        label: "First Layout",
        data: {
          category: DESIGN_CATEGORY.LAYOUT,
          type: LAYOUT_TYPES.LAYOUT_SIZE,
        },
      },
      {
        label: "Second Layout",
        data: {
          category: DESIGN_CATEGORY.LAYOUT,
          type: LAYOUT_TYPES.LAYOUT_BODY,
        },
      },
    ],
  };

  const shapesStep = {
    icon: COLORS_ICON_URL,
    label: DESIGN_CATEGORY.SHAPE,
    subSteps: [] as ShapeStep[],
  };

  const materialsStep = {
    icon: MATERIALS_ICON_URL,
    label: DESIGN_CATEGORY.MATERIAL,
    subSteps: [] as MaterialStep[],
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handleOpenSnackbar } = useSnackbar();
  const [steps, setSteps] = useState<
    (typeof layoutStep | typeof shapesStep | typeof materialsStep)[]
  >([layoutStep]);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const {
    currentStepData,
    currentStepTitle,
    isFinalStep,
    currentStep,
    nextButtonDisabled,
    prevButtonDisabled,
    nextStep,
    previousStep,
  } = useStepper();

  const { prevRoute = "/" } = useSelector((state: { app: any }) => state.app);

  const {
    layout_body: selectedLayoutBody,
    layout_size: selectedLayoutSize,
    shapes: selectedShapes,
    materials: selectedMaterials,
    composition,
    prices: pricesTable,
    pricePerStep: totalPricePerStep,
    priceRangePercentagePerStep: totalPriceRangePercentagePerStep,
    priceMode,
  } = useSelector((state: { design: DesignState }) => state.design);

  const isLayout = currentStepData?.category === DESIGN_CATEGORY.LAYOUT;
  const isLayoutBody = currentStepData?.type === LAYOUT_TYPES.LAYOUT_BODY;
  const isLayoutSize = currentStepData?.type === LAYOUT_TYPES.LAYOUT_SIZE;
  const isShape = currentStepData?.category === DESIGN_CATEGORY.SHAPE;
  const isMaterial = currentStepData?.category === DESIGN_CATEGORY.MATERIAL;

  const { data: elements = [], isLoading: isLoadingLayoutElements } = useQuery({
    queryKey: ELEMENTS_QUERY_KEY(
      selectedLayoutBody?.id || 0,
      selectedLayoutSize?.id || 0
    ),
    queryFn: () =>
      getElementsByLayout(
        selectedLayoutBody?.id || 0,
        selectedLayoutSize?.id || 0
      ),
    refetchOnWindowFocus: false,
    onError: (error) => {
      handleOpenSnackbar(getErrorMessage(error), "error");
    },

    staleTime: Infinity,
    enabled: Boolean(selectedLayoutBody?.id && selectedLayoutSize?.id),
  });

  const {
    isGettingDesignOptions,
    materials: materialsOptions,
    shapes: shapesOptions,
  } = useGetDesignStepOptions({
    layout: (selectedLayoutSize?.code || "") + (selectedLayoutBody?.code || ""),
    design_composition: composition || undefined,
    step_info: {
      step_type: isMaterial ? "MT" : "SH",
      number_of_shapes: elements.length,
      shape_number: currentStepData?.elementOrder,
      material_number: isMaterial ? currentStepData?.materialOrder : undefined,
    },
    enabled: Boolean(
      selectedLayoutBody?.id &&
        selectedLayoutSize?.id &&
        currentStepData?.category &&
        !isLayout &&
        elements.length
    ),
  });

  const currentStepOptions = useMemo(() => {
    if (isShape) return shapesOptions;
    if (isMaterial) return materialsOptions;
    if (isLayoutBody) return body_layout_items || [];
    if (isLayoutSize) return size_layout_items || [];
    return [];
  }, [
    isShape,
    isMaterial,
    isLayoutBody,
    isLayoutSize,
    shapesOptions,
    materialsOptions,
    body_layout_items,
    size_layout_items,
  ]);

  const filterOptions = useMemo(() => {
    if (isShape) return shapesOptions;
    if (isMaterial) return materialsOptions;
    return EMPTY_OPTIONS;
  }, [isShape, isMaterial, shapesOptions, materialsOptions]);

  const isNextStepDisabled =
    (isLayoutBody && !selectedLayoutBody) ||
    (isLayoutSize && !selectedLayoutSize) ||
    (isShape && selectedShapes[currentStepData?.elementOrder] === undefined) ||
    (isMaterial &&
      selectedMaterials[
        `${currentStepData?.elementOrder}_${currentStepData?.materialOrder}`
      ] === undefined);

  const { filteredOptions } = useFilter(filterOptions);

  const selectLayout = useCallback(
    (layout: LayoutSize | LayoutBody) => {
      if (isLayoutBody) {
        dispatch(
          updateCompositionWithLayout({
            layoutBody: { ...layout } as LayoutBody,
          })
        );
      } else if (isLayoutSize) {
        dispatch(
          updateCompositionWithLayout({
            layoutSize: { ...layout } as LayoutSize,
          })
        );
      }
    },
    [dispatch, isLayoutBody, isLayoutSize]
  );

  const selectShape = useCallback(
    (selectedShape: Shape, elementOrder: number) => {
      if (selectedShapes[elementOrder]?.code !== selectedShape.code) {
        dispatch(
          updateCompositionWithShape({
            shape: selectedShape,
            elementOrder,
          })
        );
      }
    },
    [dispatch, selectedShapes]
  );

  const selectMaterial = useCallback(
    (
      selectedMaterial: Material,
      elementOrder: number,
      materialOrder: number
    ) => {
      if (
        selectedMaterials[`${elementOrder}_${materialOrder}`]?.code !==
        selectedMaterial.code
      ) {
        dispatch(
          updateCompositionWithMaterial({
            material: selectedMaterial,
            elementOrder,
            materialOrder,
          })
        );
      }
    },
    [dispatch, selectedMaterials]
  );

  useEffect(() => {
    if (elements.length > 0 && composition) {
      const [shapesSteps, materialsSteps] =
        generateDesignShapesAndMaterialsSteps(elements, composition);

      setSteps([
        layoutStep,
        { ...shapesStep, subSteps: shapesSteps },
        { ...materialsStep, subSteps: materialsSteps },
      ]);
    }
  }, [elements, composition]);

  useEffect(() => {
    const shapeKey = currentStepData?.elementOrder;
    const materialKey = `${currentStepData?.elementOrder}_${currentStepData?.materialOrder}`;

    if (
      !isGettingDesignOptions &&
      isShape &&
      shapesOptions.length > 0 &&
      (!selectedShapes[shapeKey] ||
        !isShapeInComposition(
          composition,
          selectedShapes[shapeKey]?.code || ""
        ))
    ) {
      const compositionShapesCodes = getShapesFromComposition(composition);

      const shapeInComposition = shapesOptions.find(
        (s) =>
          s.code === compositionShapesCodes[currentStepData?.elementOrder - 1]
      ) as Shape | undefined;

      if (shapeInComposition) {
        selectShape(
          shapeInComposition,
          currentStepData?.elementOrder as number
        );
      }
    }

    if (
      !isGettingDesignOptions &&
      isMaterial &&
      materialsOptions.length > 0 &&
      (!selectedMaterials[materialKey] ||
        !isMaterialInComposition(
          composition,
          selectedMaterials[materialKey]?.code || ""
        ))
    ) {
      const compositionMaterialsCodes =
        getMaterialsFromComposition(composition);

      const materialInComposition = materialsOptions.find(
        (m) =>
          m.code ===
          compositionMaterialsCodes[currentStepData?.elementOrder - 1][
            currentStepData?.materialOrder - 1
          ]
      ) as Material | undefined;

      if (materialInComposition) {
        selectMaterial(
          materialInComposition,
          currentStepData?.elementOrder as number,
          currentStepData?.materialOrder as number
        );
      }
    }
  }, [
    isGettingDesignOptions,
    shapesOptions,
    materialsOptions,
    composition,
    currentStepData,
  ]);

  return (
    <div
      className={`relative col-span-3 flex w-full flex-col space-y-5 ${
        isLayout && "mx-auto max-w-[1000px]"
      }`}
    >
      {isLoadingLayoutElements && (
        <div className="absolute left-0 top-0 z-[1] !m-0 flex h-full w-full items-center justify-center">
          <AiOutlineLoading3Quarters
            size={50}
            className="shrink-0 animate-spin text-primary"
          />
        </div>
      )}
      <div className="flex flex-col space-y-3">
        <span className="hidden text-[34px] font-bold text-primary lg:block">
          {item?.name} Design
        </span>
        <Stepper
          steps={steps}
          disableNext={isNextStepDisabled}
          disableJumping={isNextStepDisabled}
        />
      </div>
      <div className="flex flex-col space-y-3">
        {isFinalStep ? (
          <DesignSummary elements={elements} />
        ) : (
          <>
            <div className="flex items-center justify-between text-primary">
              <span className="text-[18px] font-bold">
                {currentStepData?.category}
              </span>
              <div className="flex items-center mr-1 space-x-5">
                {!isLayout && (
                  <OptionsShuffle
                    options={filteredOptions}
                    selectShape={selectShape}
                    selectMaterial={selectMaterial}
                  />
                )}
                {(isMaterial || isShape) && (
                  <OptionsFilter
                    open={isFilterDialogOpen}
                    onClose={() => setIsFilterDialogOpen(false)}
                    toggle={() => setIsFilterDialogOpen((prev) => !prev)}
                  />
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-[14px] text-primary">
                {currentStepTitle}
              </span>
              {currentStepData?.elementNotes && (
                <span className="text-[13px] text-secondary">
                  {currentStepData?.elementNotes}
                </span>
              )}
            </div>
            {isShape && <DimensionFilter options={shapesOptions} />}
            {!isLayout && !!currentStepTitle && (
              <FavoritesPanel
                options={filteredOptions}
                selectShape={selectShape}
                selectMaterial={selectMaterial}
              />
            )}
            <OptionsContainer
              options={
                isLayout
                  ? (currentStepOptions as LayoutBody[] | LayoutSize[])
                  : (filteredOptions as Shape[] | Material[])
              }
              isLoadingOptions={isGettingDesignOptions}
              selectLayout={selectLayout}
              selectShape={selectShape}
              selectMaterial={selectMaterial}
            />
          </>
        )}
      </div>
      <div className="flex items-center justify-between text-[18px] lg:justify-evenly">
        <div
          className={`flex items-center space-x-2 cursor-pointer text-highlight hover:text-red-700 hover:underline underline-offset-2`}
          onClick={
            prevButtonDisabled ? () => navigate(prevRoute) : previousStep
          }
        >
          <MdKeyboardArrowLeft size={25} />
          <span>Previous</span>
        </div>
        <div
          className={`flex items-center space-x-2 ${
            nextButtonDisabled
              ? "cursor-default text-softGray"
              : "cursor-pointer text-highlight hover:text-red-700 hover:underline underline-offset-2"
          }`}
          onClick={nextStep}
        >
          <span>Next</span>
          <MdKeyboardArrowRight size={25} />
        </div>
      </div>
      {isMaxWidth1024 && (
        <>
          {!isFinalStep && (
            <>
              <Table rows={pricesTable.slice(0, currentStep)} />
              <div className="flex items-center justify-between px-5 text-primary">
                <span className="text-[18px] font-medium">Total Price</span>
                <span className="text-[24px] font-bold">
                  {formatToCurrency(
                    totalPricePerStep[currentStep - 1],
                    CURRENCY,
                    totalPriceRangePercentagePerStep[currentStep - 1],
                    priceMode
                  )}
                </span>
              </div>
            </>
          )}
          <ServiceInfo itemId={item?.id} />
        </>
      )}
    </div>
  );
}

export default DesignsSelector;
